import { Field, Float, ID, InputType } from 'type-graphql'
import { ActionArgs } from '../../action/ActionArgs'
import type { AiCreditTransaction } from './AiCreditTransaction'

@InputType()
export class DebitCreditsArgs extends ActionArgs {
  /** Generic owner of the balance — a `User._id` or `Tenant._id`, per `resolveBillingOwnerId`. */
  @Field(() => ID)
  ownerId: string

  /** Positive number of credits to debit — the account balance decreases by this amount. */
  @Field(() => Float)
  credits: number

  @Field(() => String)
  currency: string

  @Field(() => String, { nullable: true })
  feature?: string

  /** Caller-supplied human-readable description of the charge, persisted onto `AiCreditTransaction.remarks`. */
  @Field(() => String, { nullable: true })
  remarks?: string

  /** Deterministic idempotency key for a caller that may retry the same billable unit of work (e.g. `${batchId}:${chunkIndex}`). */
  @Field(() => String, { nullable: true })
  chargeKey?: string

  // --- Usage-detail snapshot, persisted onto the created `AiCreditTransaction` row (merged from
  // what used to be a separate `AiUsageRecord` document). `RecordAndDebitAiUsage` always supplies
  // the full set for a metered-call debit; left optional since `DebitCredits`' own contract is a
  // plain balance debit. ---

  @Field(() => String, { nullable: true })
  model?: string

  @Field(() => Float, { nullable: true })
  inputTokens?: number

  @Field(() => Float, { nullable: true })
  outputTokens?: number

  @Field(() => Float, { nullable: true })
  rawUsd?: number

  @Field(() => Float, { nullable: true })
  unitsPerUsd?: number

  @Field(() => Float, { nullable: true })
  markupPct?: number

  @Field(() => Float, { nullable: true })
  rawCcy?: number

  @Field(() => Float, { nullable: true })
  billedCcy?: number
}

/**
 * Atomically decrements a `CreditAccount.balance` and records a `Debit` `AiCreditTransaction`.
 * When `chargeKey` is set, the balance is gated exactly-once by a conditional UPDATE against the
 * owner's CreditAccount — never a prior read, and never an INSERT that must not duplicate — so a
 * concurrent duplicate execution of the same billable unit of work (e.g. a re-delivered BullMQ
 * batch chunk) can debit at most once; see the `DebitCredits` class doc for the full mechanism.
 * Module-internal to `ai-node` — injected by class (composed by `RecordAndDebitAiUsage`), never
 * barrel-exported and never bound to an `IocKey` symbol.
 */
export interface IDebitCredits {
  execute(args: DebitCreditsArgs): Promise<AiCreditTransaction>
}
